import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV, SITE } from "@/lib/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 backdrop-blur-xl transition-colors duration-300 ${
        scrolled
          ? "bg-[#F5E6C0]/90 border-b border-[#D9C48A]"
          : "bg-[#F5E6C0]/70 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_storici-hub/artifacts/uca8nbfn_Lista%20logo%20clienti%20%288%29%202.PNG"
            alt="Allevamento Colli Storici"
            className="h-14 sm:h-16 w-auto"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-6" data-testid="primary-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm font-medium link-underline pb-1 transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-[#7A1F1F]"
                    : "text-[#1A1710] hover:text-[#7A1F1F]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${SITE.phoneRaw}`}
            data-testid="header-call-btn"
            className="hidden xl:inline-flex items-center gap-2 text-sm font-semibold text-[#1A1710] hover:text-[#7A1F1F] transition-colors"
          >
            <Phone className="w-4 h-4" />
            {SITE.phoneDisplay}
          </a>
          <Link
            to="/contatti"
            data-testid="header-contact-btn"
            className="hidden sm:inline-flex bg-[#7A1F1F] text-white hover:bg-[#5C1717] rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
          >
            Contattaci
          </Link>
          <button
            aria-label="Apri menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden w-10 h-10 grid place-items-center rounded-full border border-[#D9C48A] hover:bg-[#EDD895]"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="xl:hidden border-t border-[#D9C48A] bg-[#F5E6C0]"
          data-testid="mobile-menu"
        >
          <nav className="px-6 py-6 flex flex-col gap-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl text-base ${
                    isActive
                      ? "bg-[#EDD895] text-[#7A1F1F] font-semibold"
                      : "text-[#1A1710] hover:bg-[#EDD895]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href={`tel:${SITE.phoneRaw}`}
              className="mt-2 inline-flex items-center gap-2 px-3 py-3 text-[#7A1F1F] font-semibold"
            >
              <Phone className="w-4 h-4" />
              {SITE.phoneDisplay}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
